import { forwardRef } from "react";
import PropTypes from "prop-types";
import { Button as AntButton } from "antd";
import { classNames } from "../../helpers/buttonClassNameHelper";

const HEIGHT_LARGE = 52;
const HEIGHT_MEDIUM = 44;
const HEIGHT_BACK = 48;
const SECONDARY_BUTTON_STYLE = {
  color: "var(--flow-secondary-text, var(--flow-accent, #ccb17b))",
  borderColor: "var(--flow-border-accent, var(--flow-accent, #ccb17b))",
  backgroundColor: "transparent",
};

/**
 * Eski `variant` API'si → antd Button props eşlemesi.
 * Renkler artık `index.css` `.flow-btn-*` üzerinden değil,
 * `AntdConfigProvider` token'larından (JWT/branding ile override edilebilir) gelir.
 */
const VARIANT_MAP = {
  primaryWithoutIcon:         { type: "primary", size: "middle", block: true,  height: HEIGHT_LARGE },
  primaryWithIcon:            { type: "primary", size: "middle", block: true,  height: HEIGHT_LARGE },
  secondaryWithoutIcon:       { type: "default", size: "middle", block: true,  height: HEIGHT_LARGE },
  secondaryWithIcon:          { type: "default", size: "middle", block: true,  height: HEIGHT_LARGE },
  primaryMediumWithoutIcon:   { type: "primary", size: "small",  block: true,  height: HEIGHT_MEDIUM },
  primaryMediumWithIcon:      { type: "primary", size: "small",  block: true,  height: HEIGHT_MEDIUM },
  secondaryMediumWithoutIcon: { type: "default", size: "small",  block: true,  height: HEIGHT_MEDIUM },
  secondaryMediumWithIcon:    { type: "default", size: "small",  block: true,  height: HEIGHT_MEDIUM },
  primaryBack:                { type: "primary", size: "middle", block: false, square: HEIGHT_BACK },
  secondaryBack:              { type: "default", size: "middle", block: false, square: HEIGHT_BACK },
};

const HTML_BUTTON_TYPES = new Set(["submit", "button", "reset"]);
const ANTD_BUTTON_TYPES = new Set(["primary", "default", "dashed", "text", "link"]);

function variantToAntdProps(variant) {
  const map = variant && VARIANT_MAP[variant];
  if (!map) return { style: {} };
  const style = {};
  if (map.height) style.height = map.height;
  if (map.square) {
    style.width = map.square;
    style.height = map.square;
    style.padding = 8;
  }
  if (map.type === "default") {
    Object.assign(style, SECONDARY_BUTTON_STYLE);
  }
  return { type: map.type, size: map.size, block: map.block, style };
}

/**
 * Hibrit Button: hem mevcut `variant` API'si hem antd Button props'ları
 * (`type`, `size`, `icon`, `block`, `shape`, `loading`, `href`, ...) ile çalışır.
 *
 * `type` prop'u tarihsel olarak hem HTML (`submit` / `button` / `reset`) hem
 * antd (`primary` / `default` / ...) anlamına gelebildiği için ikisini de
 * doğru kanala yönlendirir.
 */
export const Button = forwardRef(function Button(
  { className, children, variant, type, htmlType, style, block, ...props },
  ref
) {
  const hasExplicitVariant = variant !== undefined;
  const isAntdType = type && ANTD_BUTTON_TYPES.has(type);

  /**
   * `variant` açıkça verilmişse onu uygula; aksi halde antd tipleri
   * (`text` / `link` / `dashed`) ile çağrıldıysa boyut/blok uygulamadan
   * antd'in doğal davranışına bırak. Varsayılanda eski sayfa butonu
   * görünümü için `primaryWithoutIcon` yüksekliği geçerli kalır.
   */
  const effectiveVariant = hasExplicitVariant
    ? variant
    : isAntdType
      ? null
      : "primaryWithoutIcon";

  const fromVariant = effectiveVariant
    ? variantToAntdProps(effectiveVariant)
    : { style: {} };

  let resolvedAntdType = fromVariant.type;
  let resolvedHtmlType = htmlType;
  if (type) {
    if (ANTD_BUTTON_TYPES.has(type)) {
      resolvedAntdType = type;
    } else if (HTML_BUTTON_TYPES.has(type)) {
      resolvedHtmlType = type;
    }
  }

  const resolvedBlock = block !== undefined ? block : fromVariant.block;
  const isSecondaryVariant =
    effectiveVariant && VARIANT_MAP[effectiveVariant]?.type === "default";

  return (
    <AntButton
      ref={ref}
      type={resolvedAntdType}
      htmlType={resolvedHtmlType}
      size={fromVariant.size}
      block={resolvedBlock}
      style={{ ...(fromVariant.style || {}), ...(style || {}) }}
      className={classNames(isSecondaryVariant ? "flow-secondary-button" : "", className)}
      {...props}
    >
      {children}
    </AntButton>
  );
});

Button.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
  variant: PropTypes.oneOf([
    "primaryWithoutIcon",
    "primaryWithIcon",
    "secondaryWithoutIcon",
    "secondaryWithIcon",
    "primaryMediumWithoutIcon",
    "primaryMediumWithIcon",
    "secondaryMediumWithoutIcon",
    "secondaryMediumWithIcon",
    "primaryBack",
    "secondaryBack",
    "",
  ]),
  type: PropTypes.string,
  htmlType: PropTypes.oneOf(["submit", "button", "reset"]),
  style: PropTypes.object,
  block: PropTypes.bool,
};
